import { useEffect, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import type { EmployeeUserResponse, CreateEmployeeRequest, EmployeeUserAccountRequest } from '@/api/model'
import { AccountStatus } from '@/api/model'
import {
  usePostApiCompaniesCurrentEmployees,
  usePatchApiEmployeeEmployeeIdAccountmanagement,
  getGetApiCompaniesCurrentAccountmanagementEmployeesQueryKey,
} from '@/api/generated/company-management-user/company-management-user'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select'
import { notify } from '@/lib/notify'
import { GroupMultiSelect } from '@/components/common/GroupMultiSelect'
import { SubCompanySelect } from '@/components/common/SubCompanySelect'
import { usePermissions } from '@/hooks/usePermissions'
import { PermissionEntityType } from '@/api/model'

type FormValues = {
  firstName: string
  lastName: string
  employeeNumber: string
  position: string
  birthDate: string
  employmentDate: string
  status: AccountStatus
  groupIds: number[]
  subCompanyId: number | null
  password: string
}

const defaultValues: FormValues = {
  firstName: '',
  lastName: '',
  employeeNumber: '',
  position: '',
  birthDate: '',
  employmentDate: '',
  status: AccountStatus.Active,
  groupIds: [],
  subCompanyId: null,
  password: '',
}

interface Props {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  employee?: EmployeeUserResponse | null
}

export function EmployeeDialog({ isOpen, onOpenChange, employee }: Props) {
  const { t } = useTranslation(['employees', 'common'])
  const isEdit = !!employee
  const queryClient = useQueryClient()
  const { userHasPermission } = usePermissions()
  // In edit mode the form is read-only without the Edit grant; create mode is
  // gated by the create button that opened the dialog.
  const readOnly = isEdit && !userHasPermission(PermissionEntityType.Employees, 'Edit')
  const { register, handleSubmit, reset, setValue, watch } = useForm<FormValues>({ defaultValues: defaultValues })
  const [showPassword, setShowPassword] = useState(false)

  // On open dialog fill data
  useEffect(() => {
    if (isOpen) {
      reset(isEdit ? {
        firstName:      employee.firstName      ?? '',
        lastName:       employee.lastName       ?? '',
        employeeNumber: employee.employeeNumber ?? '',
        position:       employee.position       ?? '',
        birthDate:      employee.birthDate?.split('T')[0]      ?? '',
        employmentDate: employee.employmentDate?.split('T')[0] ?? '',
        status:         (employee.status as AccountStatus)     ?? AccountStatus.Active,
        groupIds:       employee.groups?.map(g => g.id!).filter(Boolean) ?? [],
        subCompanyId:   employee.subCompanyId ?? null,
      } : defaultValues)
    }
  }, [isOpen, employee])

  const invalidate = () => queryClient.invalidateQueries({
    queryKey: getGetApiCompaniesCurrentAccountmanagementEmployeesQueryKey(),
  })

  const { mutate: createEmployee, isPending: isCreating } = usePostApiCompaniesCurrentEmployees({
    mutation: {
      onSuccess: () => {
        notify.success(t('toasts.created'))
        onOpenChange(false)
        invalidate()
      },
      onError: (error) => notify.apiError(error, t('toasts.createError')),
    },
  })

  const { mutate: updateEmployee, isPending: isUpdating } = usePatchApiEmployeeEmployeeIdAccountmanagement({
    mutation: {
      onSuccess: () => {
        notify.success(t('toasts.updated'))
        onOpenChange(false)
        invalidate()
      },
      onError: (error) => notify.apiError(error, t('toasts.updateError')),
    },
  })

  const isPending = isCreating || isUpdating

  const handleOpenChange = (open: boolean) => {
    if (!open) reset(defaultValues)
    onOpenChange(open)
  }

  const onValid = (values: FormValues) => {
    if (isEdit) {
      const payload: EmployeeUserAccountRequest = {
        firstName:      values.firstName      || null,
        lastName:       values.lastName       || null,
        employeeNumber: values.employeeNumber || null,
        position:       values.position       || null,
        birthDate:      values.birthDate      || null,
        employmentDate: values.employmentDate || null,
        status:         values.status,
        groupIds:       values.groupIds,
        subCompanyId:   values.subCompanyId,
      }
      updateEmployee({ employeeId: employee!.id!, data: payload })
    } else {
      const payload: CreateEmployeeRequest = {
        firstName:      values.firstName      || null,
        lastName:       values.lastName       || null,
        employeeNumber: values.employeeNumber || null,
        position:       values.position       || null,
        birthDate:      values.birthDate      || null,
        employmentDate: values.employmentDate || null,
        status:         values.status,
        groupIds:       values.groupIds,
        subCompanyId:   values.subCompanyId,
      }
      createEmployee({ data: [{ ...payload, password: values.password || null }] })
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t('dialog.title')}</DialogTitle>
          <DialogDescription>
            {isEdit ? t('dialog.descriptionEdit') : t('dialog.descriptionCreate')}
          </DialogDescription>
        </DialogHeader>

        <form id="employee-form" onSubmit={handleSubmit(onValid)} className="flex flex-col gap-4 flex-1 overflow-y-auto px-1 py-2 scrollbar-custom">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">{t('dialog.fields.firstName')} <span className="text-destructive">*</span></Label>
              <Input id="firstName" required disabled={readOnly} {...register('firstName')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">{t('dialog.fields.lastName')} <span className="text-destructive">*</span></Label>
              <Input id="lastName" required disabled={readOnly} {...register('lastName')} />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="employeeNumber">{t('dialog.fields.employeeNumber')} <span className="text-destructive">*</span></Label>
            <Input id="employeeNumber" required disabled={readOnly} {...register('employeeNumber')} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="position">{t('dialog.fields.position')} <span className="text-destructive">*</span></Label>
            <Input id="position" required disabled={readOnly} {...register('position')} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="birthDate">{t('dialog.fields.birthDate')}</Label>
              <Input id="birthDate" type="date" className="color-scheme-light" disabled={readOnly} {...register('birthDate')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="employmentDate">{t('dialog.fields.employmentDate')}</Label>
              <Input id="employmentDate" type="date" className="color-scheme-light" disabled={readOnly} {...register('employmentDate')} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('dialog.fields.groups')}</Label>
            <GroupMultiSelect
              selectedIds={watch('groupIds')}
              onChange={ids => setValue('groupIds', ids)}
              disabled={readOnly}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('dialog.fields.subCompany')}</Label>
            <SubCompanySelect
              value={watch('subCompanyId')}
              onChange={id => setValue('subCompanyId', id)}
              disabled={readOnly}
            />
          </div>

          {!isEdit && (
            <div className="space-y-2">
              <Label htmlFor="password">{t('dialog.fields.password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className="pr-10"
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>{t('dialog.fields.status')}</Label>
            <Select value={watch('status')} onValueChange={(v) => setValue('status', v as AccountStatus)} disabled={readOnly}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={AccountStatus.Active}>{t('status.active', { ns: 'common' })}</SelectItem>
                <SelectItem value={AccountStatus.Blocked}>{t('status.blocked', { ns: 'common' })}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>{t('buttons.cancel', { ns: 'common' })}</Button>
          <Button type="submit" form="employee-form" disabled={isPending || readOnly}>{isPending ? t('buttons.saving', { ns: 'common' }) : t('buttons.save', { ns: 'common' })}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
